<template>
  <div>
    <filter-bar
      :filters="filterSchema"
      :initial-value="filterValues"
      :result-count="totalRows"
      :per-page="perPage"
      :per-page-options="perPageOptions"
      @change="onFiltersChanged"
      @update:perPage="onPerPageChange"
    >
      <template #actions>
        <base-button v-if="hasDownloadPermission" icon class="btn-link text-white" @click="downloadCSV()">
          <i class="tim-icons icon-cloud-download-93"></i>
        </base-button>
      </template>
    </filter-bar>

    <!-- Loading skeleton -->
    <div v-if="loading" class="tag-list mt-3">
      <div v-for="n in 3" :key="'skel' + n" class="tag-row d-flex align-items-center">
        <span class="skeleton-dot mr-2"></span>
        <span class="skeleton-text flex-grow-1"></span>
        <span class="skeleton-btn"></span>
      </div>
    </div>

    <!-- Empty state: no tags at all -->
    <div v-else-if="totalRows === 0 && !hasActiveFilters" class="text-center mt-4 mb-2">
      <img src="/img/pandas/empty-custom-fields.png" alt="No tags" class="empty-state-img empty-state-img--md" />
      <h4 class="mt-3 empty-state-text">No tags yet</h4>
      <p class="tag-empty-hint">
        Tags let you label bookings for segmentation, like corporate, birthday party, or walk in, and filter and report on them later
      </p>
    </div>

    <!-- No results from filters -->
    <div v-else-if="totalRows === 0 && hasActiveFilters" class="text-center mt-4 mb-2">
      <img src="/img/pandas/empty-custom-fields.png" alt="No results" class="empty-state-img empty-state-img--md" />
      <p class="tag-empty-hint mt-3">No tags match your filters</p>
    </div>

    <!-- Tag list -->
    <div v-else class="tag-list mt-3">
      <div
        v-for="tag in tags"
        :key="'TAG' + tag.id"
        class="tag-row d-flex align-items-center"
        :class="{ 'tag-row--archived': tag.is_archived }"
        @click="openEditDrawer(tag)"
      >
        <div class="tag-row__info flex-grow-1">
          <span class="tag-row__name text-white">
            <span class="tag-color-dot mr-1" :style="{ backgroundColor: tag.color }"></span>
            {{ tag.name }}
            <span v-if="tag.also_tag_customers" class="tag-row__fanout-badge">also tags customers</span>
            <span v-if="tag.apply_to_online_bookings" class="tag-row__fanout-badge">online bookings</span>
            <span v-if="tag.is_archived" class="tag-row__archived-badge">Archived</span>
          </span>
          <small v-if="tag.description" class="d-block text-truncate tag-row__desc">
            {{ tag.description }}
          </small>
        </div>
        <el-dropdown trigger="click" @click.native.stop>
          <base-button link type="primary" size="sm">
            <i style="font-size: large" class="tim-icons el-icon-more"></i>
          </base-button>
          <template #dropdown>
            <el-dropdown-menu>
              <a class="dropdown-item text-default mb-2" href="#" @click.prevent="openEditDrawer(tag)">
                <i class="tim-icons text-default icon-pencil mr-2"></i> Edit
              </a>
              <hr class="my-1">
              <a v-if="!tag.is_archived" class="dropdown-item text-default" href="#" @click.prevent="archiveTag(tag)">
                <i class="tim-icons text-default icon-trash-simple mr-2"></i> Archive
              </a>
              <a v-else class="dropdown-item text-default" href="#" @click.prevent="restoreTag(tag)">
                <i class="tim-icons text-default icon-refresh-02 mr-2"></i> Restore
              </a>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>

    <b-pagination
      v-if="totalRows > 0"
      v-model="currentPage"
      :total-rows="totalRows"
      :per-page="perPage"
      class="mt-3 mb-1"
      @input="getTags()"
    ></b-pagination>

    <!-- Create/edit tag drawer -->
    <base-drawer
      :show="showDrawer"
      :title="isEditing ? 'Edit tag' : 'Create tag'"
      @close="closeDrawer"
    >
      <drawer-section title="Tag details" icon="icon-tag">
        <base-input
          required
          label="Name"
          type="text"
          placeholder="e.g. Corporate"
          v-model="selectedTag.name"
          :error="errs.name"
          maxlength="100"
        ></base-input>

        <label>Description</label>
        <el-input
          v-model="selectedTag.description"
          :rows="2"
          type="textarea"
          placeholder="Optional description"
          maxlength="255"
          class="mb-3"
        ></el-input>

        <label class="d-block mb-2">Color</label>
        <div class="swatch-picker">
          <button
            v-for="color in colorPresets"
            :key="color"
            type="button"
            class="swatch-picker__swatch"
            :class="{ 'swatch-picker__swatch--active': selectedTag.color === color }"
            :style="{ backgroundColor: color }"
            :aria-label="'Color ' + color"
            @click="selectedTag.color = color"
          ></button>
          <el-color-picker
            v-model="selectedTag.color"
            size="small"
            class="swatch-picker__more"
          ></el-color-picker>
        </div>
      </drawer-section>

      <drawer-section title="Automation" icon="icon-spaceship">
        <div class="d-flex align-items-center" style="gap: 10px;">
          <el-switch
            :value="!!selectedTag.also_tag_customers"
            active-color="#e14eca"
            @change="selectedTag.also_tag_customers = $event"
          ></el-switch>
          <label class="mb-0">Tag a booking's customers automatically</label>
        </div>
        <info-hint variant="primary" class="mt-2" style="width: 100%">
          Tags can be applied to bookings and to customers independently. With this on, applying this tag to a booking <strong>also applies it to every customer on that booking</strong>, including everyone who checked in with a waiver. Email audiences that filter by this tag <strong>will include those customers</strong>, so mass emails can be affected. Removing the tag from a booking does not remove it from the customers.
        </info-hint>
        <div class="d-flex align-items-center mt-3" style="gap: 10px;">
          <el-switch
            :value="!!selectedTag.apply_to_online_bookings"
            active-color="#e14eca"
            @change="selectedTag.apply_to_online_bookings = $event"
          ></el-switch>
          <label class="mb-0">Apply to online bookings automatically</label>
        </div>
        <info-hint variant="primary" class="mt-2" style="width: 100%">
          With this on, every booking made through the booking site gets this tag the moment it is created. If customer tagging above is also on, the booking's customer is tagged at the same time. Bookings created by staff from the console are not affected.
        </info-hint>
      </drawer-section>

      <template #footer>
        <div class="footer-actions">
          <base-button type="secondary" size="sm" @click="closeDrawer">Cancel</base-button>
          <base-button
            type="primary"
            size="sm"
            @click="saveTag"
            :loading="saving"
            :disabled="!selectedTag.name || !selectedTag.name.trim()"
          >
            {{ isEditing ? 'Update' : 'Create' }}
          </base-button>
        </div>
      </template>
    </base-drawer>
  </div>
</template>

<script setup lang="ts">
import { COLOR_PRESETS } from '@/util/colorPresets';
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import swal from 'sweetalert2';
import { API_LOCATION } from '@/api-config';

interface Tag {
  id?: number;
  name: string;
  description: string | null;
  color: string;
  is_archived?: number;
  also_tag_customers: number | boolean;
  apply_to_online_bookings?: number | boolean;
}

const DEFAULT_COLOR = '#1d8cf8';

const companyId = localStorage.getItem('companyId');
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id;
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/tags`;

const loading = ref(false);
const saving = ref(false);
const tags = ref<Tag[]>([]);
const totalRows = ref(0);
const currentPage = ref(1);
const perPage = ref(10);
const perPageOptions = [10, 25, 50];
const filterValues = ref<{ name: string; archived: boolean }>({ name: '', archived: false });
const showDrawer = ref(false);
const isEditing = ref(false);
const selectedTag = ref<Tag>({ name: '', description: '', color: DEFAULT_COLOR, also_tag_customers: 0 });
const errs = ref({ name: '' });

const colorPresets = COLOR_PRESETS;

const filterSchema = computed(() => [
  { key: 'name', type: 'text', label: 'Name', placeholder: 'Search by name', operatorLabel: 'contains', primary: true },
  { key: 'archived', type: 'toggle', label: 'Show archived', primary: true },
]);

const hasActiveFilters = computed(() => !!filterValues.value.name || !!filterValues.value.archived);

const hasDownloadPermission = computed(() => {
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  return permissions.some((p: string) => p === 'download_data');
});

// Component instance proxy for $notify/$router access from setup scope.
const vm = getCurrentInstance()!.proxy as any;

async function getAuthToken() {
  const user = await Auth.currentAuthenticatedUser();
  return user.signInUserSession.idToken.jwtToken;
}

function buildListUrl(count: number, offset: number) {
  let url = `${baseUrl}?count=${count}&offset=${offset}`;
  if (!filterValues.value.archived) url += '&archived=0';
  if (filterValues.value.name) url += `&name=${encodeURIComponent(filterValues.value.name)}`;
  return url;
}

async function getTags() {
  loading.value = true;
  try {
    const token = await getAuthToken();
    const config = { headers: { Authorization: token } };
    const response = await axios.get(buildListUrl(perPage.value, (currentPage.value - 1) * perPage.value), config);
    tags.value = response.data.tags || [];
    totalRows.value = response.data.total_rows || 0;
  } catch (err: any) {
    if (err.response && err.response.status === 401) {
      vm.$router.push('/forbidden');
    } else {
      vm.$notify({ type: 'danger', message: 'Failed to load tags', timeout: 3000 });
    }
  } finally {
    loading.value = false;
  }
}

function onFiltersChanged(values: any) {
  filterValues.value = { name: values.name || '', archived: !!values.archived };
  currentPage.value = 1;
  getTags();
}

function onPerPageChange(value: number) {
  perPage.value = value;
  currentPage.value = 1;
  getTags();
}

function openAddForm() {
  isEditing.value = false;
  selectedTag.value = { name: '', description: '', color: DEFAULT_COLOR, also_tag_customers: 0, apply_to_online_bookings: 0 };
  errs.value.name = '';
  showDrawer.value = true;
}

function openEditDrawer(tag: Tag) {
  isEditing.value = true;
  selectedTag.value = {
    id: tag.id,
    name: tag.name,
    description: tag.description || '',
    color: tag.color || DEFAULT_COLOR,
    is_archived: tag.is_archived ? 1 : 0,
    also_tag_customers: tag.also_tag_customers ? 1 : 0,
    apply_to_online_bookings: tag.apply_to_online_bookings ? 1 : 0,
  };
  errs.value.name = '';
  showDrawer.value = true;
}

function closeDrawer() {
  showDrawer.value = false;
  isEditing.value = false;
  errs.value.name = '';
}

// Backend error bodies are JSON ({ error }); fall back to a generic message.
function apiErrorMessage(err: any, fallback: string) {
  const data = err?.response?.data;
  if (data && typeof data === 'object' && data.error) return data.error;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      if (parsed && parsed.error) return parsed.error;
    } catch (e) { /* not JSON */ }
  }
  return fallback;
}

async function saveTag() {
  if (!selectedTag.value.name || !selectedTag.value.name.trim()) {
    errs.value.name = 'Name is required';
    return;
  }
  errs.value.name = '';
  saving.value = true;

  try {
    const token = await getAuthToken();
    const config = { headers: { Authorization: token } };
    const data: any = {
      name: selectedTag.value.name.trim(),
      description: selectedTag.value.description ? String(selectedTag.value.description).trim() : null,
      color: selectedTag.value.color || DEFAULT_COLOR,
      also_tag_customers: selectedTag.value.also_tag_customers ? 1 : 0,
      apply_to_online_bookings: selectedTag.value.apply_to_online_bookings ? 1 : 0,
    };

    if (isEditing.value) {
      await axios.put(`${baseUrl}/${selectedTag.value.id}`, data, config);
      vm.$notify({ type: 'success', message: 'Tag updated', timeout: 3000 });
    } else {
      await axios.post(baseUrl, data, config);
      vm.$notify({ type: 'success', message: 'Tag created', timeout: 3000 });
    }

    closeDrawer();
    await getTags();
  } catch (err: any) {
    if (err.response && err.response.status === 401) {
      vm.$router.push('/forbidden');
    } else if (err.response && err.response.status === 400) {
      errs.value.name = apiErrorMessage(err, 'Could not save the tag');
    } else {
      swal('Error', 'Something went wrong', 'error');
    }
  } finally {
    saving.value = false;
  }
}

async function archiveTag(tag: Tag) {
  const result = await swal({
    title: 'Are you sure?',
    text: `"${tag.name}" will be archived. Bookings and customers keep this tag; it just leaves the picker.`,
    type: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Archive',
    cancelButtonText: 'Cancel',
  });
  if (!result.value) return;

  loading.value = true;
  try {
    const token = await getAuthToken();
    const config = { headers: { Authorization: token } };
    await axios.delete(`${baseUrl}/${tag.id}`, config);
    vm.$notify({ type: 'success', message: 'Tag archived', timeout: 3000 });
    await getTags();
  } catch (err: any) {
    if (err.response && err.response.status === 401) {
      vm.$router.push('/forbidden');
    } else {
      swal('Error', 'Something went wrong', 'error');
    }
    loading.value = false;
  }
}

async function restoreTag(tag: Tag) {
  try {
    const token = await getAuthToken();
    const config = { headers: { Authorization: token } };
    await axios.put(`${baseUrl}/${tag.id}`, {
      name: tag.name,
      description: tag.description || null,
      color: tag.color || DEFAULT_COLOR,
      also_tag_customers: tag.also_tag_customers ? 1 : 0,
      is_archived: 0,
    }, config);
    vm.$notify({ type: 'success', message: 'Tag restored', timeout: 3000 });
    await getTags();
  } catch (err: any) {
    if (err.response && err.response.status === 401) {
      vm.$router.push('/forbidden');
    } else if (err.response && err.response.status === 400) {
      vm.$notify({ type: 'danger', message: apiErrorMessage(err, 'Could not restore the tag'), timeout: 4000 });
    } else {
      swal('Error', 'Something went wrong', 'error');
    }
  }
}

// Full export across pages, then a client-built CSV (house pattern).
async function downloadCSV() {
  try {
    const token = await getAuthToken();
    const config = { headers: { Authorization: token } };
    const all: Tag[] = [];
    let offset = 0;
    const batch = 100;
    for (;;) {
      const response = await axios.get(buildListUrl(batch, offset), config);
      const page = response.data.tags || [];
      all.push(...page);
      offset += batch;
      if (all.length >= (response.data.total_rows || 0) || page.length === 0) break;
    }

    const esc = (v: any) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    let csv = 'Name,Description,Color,Also tags customers,Applies to online bookings,Archived\n';
    all.forEach((t) => {
      csv += [esc(t.name), esc(t.description), esc(t.color), t.also_tag_customers ? 'Yes' : 'No', t.apply_to_online_bookings ? 'Yes' : 'No', t.is_archived ? 'Yes' : 'No'].join(',') + '\n';
    });

    const anchor = document.createElement('a');
    anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    anchor.target = '_blank';
    anchor.download = 'tags.csv';
    anchor.click();
  } catch (err) {
    vm.$notify({ type: 'danger', message: 'Failed to export tags', timeout: 3000 });
  }
}

onMounted(() => {
  getTags();
});

// The parent page's header row owns the Create tag and download buttons (they
// sit on the tab rail line); script-setup exposes nothing by default.
defineExpose({ openAddForm, downloadCSV });
</script>

<script lang="ts">
import { ColorPicker, Dropdown, DropdownMenu, DropdownItem, Input, Switch } from 'element-ui';
import { BPagination } from 'bootstrap-vue';
import FilterBar from '@/components/FilterBar.vue';
import InfoHint from '@/components/InfoHint.vue';

export default {
  components: {
    [ColorPicker.name]: ColorPicker,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [Input.name]: Input,
    [Switch.name]: Switch,
    BPagination,
    FilterBar,
    InfoHint,
  },
};
</script>

<style lang="scss" scoped>
.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  width: 100%;
}

.tag-row {
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  margin-bottom: 6px;
  background: rgba(255, 255, 255, 0.02);
  transition: background 0.15s;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  &--archived {
    opacity: 0.65;
  }

  &__name {
    font-weight: 600;
    font-size: 14px;
  }

  &__desc {
    max-width: 300px;
    color: rgba(255, 255, 255, 0.7);
  }

  &__fanout-badge {
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #e14eca;
    background: rgba(225, 78, 202, 0.15);
    border-radius: 10px;
    padding: 2px 8px;
    margin-left: 8px;
    vertical-align: middle;
  }

  &__archived-badge {
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: rgba(255, 255, 255, 0.6);
    background: rgba(125, 125, 125, 0.2);
    border-radius: 10px;
    padding: 2px 8px;
    margin-left: 8px;
    vertical-align: middle;
  }
}

.tag-color-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
  vertical-align: middle;
  border: 1px solid rgba(125, 125, 125, 0.35);
}

.tag-empty-hint {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;
  max-width: 420px;
  margin: 0 auto;
}

// Skeleton styles
@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.skeleton-dot, .skeleton-text, .skeleton-btn {
  display: inline-block;
  border-radius: 4px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.05) 25%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.05) 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s ease-in-out infinite;
}

.skeleton-dot { width: 14px; height: 14px; }
.skeleton-text { height: 16px; max-width: 140px; }
.skeleton-btn { width: 28px; height: 20px; }

// Light mode
.white-content {
  .tag-row {
    border-color: rgba(0, 0, 0, 0.08);
    background: rgba(0, 0, 0, 0.03);

    &:hover {
      background: rgba(0, 0, 0, 0.06);
    }

    &__desc {
      color: rgba(0, 0, 0, 0.6);
    }

    &__archived-badge {
      color: rgba(0, 0, 0, 0.55);
      background: rgba(0, 0, 0, 0.08);
    }
  }

  .tag-empty-hint {
    color: rgba(0, 0, 0, 0.55);
  }

  .skeleton-dot, .skeleton-text, .skeleton-btn {
    background: linear-gradient(90deg, #e9ecef 25%, #f5f6fa 50%, #e9ecef 75%);
    background-size: 200% 100%;
    animation: skeleton-shimmer 1.5s ease-in-out infinite;
  }
}
</style>
